// FILE PATH: eRFQFrontend/src/component/Vendor/LogisticBidSubmission/LogisticStep2.jsx

import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import React, { useMemo, useRef, useState } from "react";
import ErrorModal from "../../../utils/ErrorModal";
import * as XLSX from "xlsx";

const units = ["KGS", "CBM", "Shipment", "Invoice", "Vehicle", "Container"];

const createEmptyItem = (itemsLength = 0) => ({
  srNo: itemsLength + 1,
  itemOrder: itemsLength + 1,
  particulars: "",
  unit: "",
  quantity: "",
  currency: "",
  exchangeRate: "",
  rate: "",
  total: 0,
  remark: "",
  isAdditionalItem: true,
});

const LogisticStep2 = ({ rfq, items, setItems, setStep, previousQuote }) => {
  const [errorModal, setErrorModal] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const fileInputRef = useRef(null);
  const currencyOptions = Array.isArray(rfq?.currencies) ? rfq.currencies : [];
  const [isImporting, setIsImporting] = useState(false);

  const dropdownSx = {
    "& .MuiInputBase-root": {
      minHeight: "45px",
      fontSize: "12px",
    },
    "& input": {
      padding: "15px",
    },
  };

  const paperSx = {
    sx: {
      background: "#D8E9F0",
      "& .MuiAutocomplete-option": {
        fontSize: "12px",
        padding: "6px 12px",
        textAlign: "start",
      },
    },
  };

  const handleFileSelect = () => {
    fileInputRef.current.click();
  };

  const formatAmount = (value) =>
    new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(parseFloat(value || 0));

  const getNumericValue = (value, fallback = 0) => {
    if (value === null || value === undefined || value === "") return fallback;
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? fallback : parsed;
  };

  const syncComputedFields = (item) => {
    const qty = getNumericValue(item.quantity, 0);
    const rate = getNumericValue(item.rate, 0);
    const exRate = getNumericValue(item.exchangeRate, 1);

    return {
      ...item,
      total: qty * rate * exRate,
    };
  };

  const updateItem = (index, changes) => {
    const updated = [...items];
    updated[index] = syncComputedFields({
      ...updated[index],
      ...changes,
    });

    setItems(
      updated.map((item, itemIndex) => ({
        ...item,
        srNo: itemIndex + 1,
      })),
    );
  };

  const handleTextChange = (index, field, value) => {
    updateItem(index, { [field]: value });
  };

  const handleRateChange = (index, value) => {
    let nextValue = value;

    if (nextValue.includes(".")) {
      const [intPart, decPart] = nextValue.split(".");
      if (decPart.length > 2) {
        nextValue = `${intPart}.${decPart.slice(0, 2)}`;
      }
    }

    updateItem(index, { rate: nextValue });
  };

  const handleCurrencyChange = (index, newValue) => {
    const selectedCurrency = currencyOptions.find((c) => c.name === newValue);

    updateItem(index, {
      currency: newValue || "",
      exchangeRate: selectedCurrency?.rate ?? "",
    });
  };

  const handleAddRow = () => {
    setItems((prev) => [...prev, createEmptyItem(prev.length)]);
  };

  const handleDeleteRow = (index) => {
    setItems((prev) =>
      prev
        .filter((_, itemIndex) => itemIndex !== index)
        .map((item, itemIndex) => ({
          ...item,
          srNo: itemIndex + 1,
        })),
    );
  };

  const sortedItems = useMemo(
    () => [...items].sort((a, b) => a.itemOrder - b.itemOrder),
    [items],
  );

  const grandTotalInInr = sortedItems.reduce(
    (sum, item) => sum + parseFloat(item.total || 0),
    0,
  );

  const buildCurrencyTotals = (quoteItems) =>
    quoteItems.reduce((acc, item) => {
      const currencyName = item.currency || "";
      if (!currencyName) return acc;

      const amount =
        getNumericValue(item.quantity, 0) * getNumericValue(item.rate, 0);

      acc[currencyName] = (acc[currencyName] || 0) + amount;
      return acc;
    }, {});

  const currencyTotals = buildCurrencyTotals(sortedItems);

  const previousQuoteItems = Array.isArray(previousQuote?.quoteItems)
    ? previousQuote.quoteItems
        .slice()
        .sort((a, b) => (a.srNo || 0) - (b.srNo || 0))
    : [];

  const previousGrandTotalInInr = previousQuoteItems.reduce(
    (sum, item) => sum + parseFloat(item.total || 0),
    0,
  );

  const previousCurrencyTotals = buildCurrencyTotals(previousQuoteItems);

  const handleNext = () => {
    if (!sortedItems.length) {
      setErrorMsg("Please add at least one line item.");
      setErrorModal(true);
      return;
    }

    const invalidRow = sortedItems.find((item) => {
      const hasParticulars = String(item.particulars || "").trim() !== "";
      const hasUnit = String(item.unit || "").trim() !== "";
      const hasQuantity =
        item.quantity !== "" &&
        item.quantity !== null &&
        item.quantity !== undefined &&
        Number(item.quantity) > 0;
      const hasCurrency = String(item.currency || "").trim() !== "";

      return !hasParticulars || !hasUnit || !hasQuantity || !hasCurrency;
    });

    if (invalidRow) {
      setErrorMsg(
        "Please enter Particulars, UOM, Quantity, and Currency for every line item.",
      );
      setErrorModal(true);
      return;
    }

    setStep(3);
  };

  const exportToExcel = () => {
    const data = items.map((item) => ({
      Particulars: item.particulars,
      Quantity: item.quantity,
      Unit: item.unit,
      Currency: item.currency,
      Rate: item.rate,
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb, ws, "Work Items");

    XLSX.writeFile(wb, "LogisticWorkItems.xlsx");
  };

  const importFromExcel = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsImporting(true);

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "array" });
        const data = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]);

        if (!data.length) {
          setErrorMsg("Excel file is empty.");
          setErrorModal(true);
          setIsImporting(false);
          return;
        }

        // 🔥 helper to extract currency code
        const getCurrencyCode = (name) =>
          String(name || "")
            .split(" - ")[0]
            .trim()
            .toUpperCase();

        // 🔥 helper to get exchange rate
        const getExchangeRate = (currency) => {
          if (!currency) return 0;

          const code = getCurrencyCode(currency);

          const c = parsedCurrencies.find(
            (cur) => getCurrencyCode(cur.name) === code,
          );

          return c ? Number(c.rate) : 0;
        };

        setItems((prevItems) => {
          const buyerItems = prevItems.filter((i) => !i.isAdditionalItem);

          const supplierItems = [];

          data.forEach((row, i) => {
            const quantity = parseFloat(row.Quantity) || 0;
            const rate = parseFloat(row.Rate) || 0;
            const rawCurrency = row.Currency || "";

            const code = getCurrencyCode(rawCurrency);

            // find full option from dropdown
            const matchedCurrency = parsedCurrencies.find(
              (c) => getCurrencyCode(c.name) === code,
            );

            const currency = matchedCurrency ? matchedCurrency.name : "";

            const exchangeRate = getExchangeRate(currency);

            // 🔥 If row index matches buyer item → update limited fields only
            if (i < buyerItems.length) {
              const existing = buyerItems[i];

              buyerItems[i] = {
                ...existing,
                quantity: quantity || existing.quantity,
                unit: row.Unit || existing.unit,
                currency: currency || existing.currency,
                rate: rate || existing.rate,
                exchangeRate: exchangeRate || existing.exchangeRate,
                total: quantity * rate * (exchangeRate || 1),
                // ❌ DO NOT CHANGE particulars
                particulars: existing.particulars,
              };
            } else {
              // 🔥 New supplier-added item
              supplierItems.push({
                srNo: buyerItems.length + supplierItems.length + 1,
                itemOrder: buyerItems.length + supplierItems.length + 1,
                particulars: row.Particulars || "",
                quantity,
                unit: row.Unit || "",
                currency,
                rate,
                exchangeRate,
                total: quantity * rate * exchangeRate,
                remark: "",
                isAdditionalItem: true,
              });
            }
          });

          // 🔥 Merge final result
          return [...buyerItems, ...supplierItems].map((item, idx) => ({
            ...item,
            srNo: idx + 1,
            itemOrder: idx + 1,
          }));
        });

        setIsImporting(false);
        event.target.value = null;
      } catch (err) {
        console.error("Import error:", err);
        setErrorMsg("Invalid Excel format.");
        setErrorModal(true);
        setIsImporting(false);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const parsedCurrencies = useMemo(() => {
    if (!rfq.currencies) return [];
    if (typeof rfq.currencies === "string") {
      try {
        return JSON.parse(rfq.currencies);
      } catch {
        return [];
      }
    }
    return Array.isArray(rfq.currencies) ? rfq.currencies : [];
  }, [rfq.currencies]);

  return (
    <div style={{ position: "relative" }}>
      <h2>Work Items Rate Entry</h2>

      {previousQuoteItems.length > 0 && (
        <div className="dashboard-container">
          <Typography className="form-title">Your Previous Quote</Typography>
          <table
            border={1}
            style={{
              borderCollapse: "collapse",
              minWidth: "100%",
              marginTop: "10px",
              textAlign: "center",
            }}
          >
            <thead>
              <tr>
                <th style={{ padding: "20px" }}>Sr.</th>
                <th style={{ width: "240px" }}>Particulars</th>
                <th>UOM</th>
                <th>Quantity</th>
                <th>Currency</th>
                {/* <th>Ex. Rate</th> */}
                <th style={{ width: "150px" }}>Rate</th>
                <th style={{ width: "220px" }}>Remark</th>
                <th>Total (INR)</th>
              </tr>
            </thead>
            <tbody>
              {previousQuoteItems
                .sort((a, b) => a.srNo - b.srNo)
                .map((item, idx) => (
                  <tr
                    key={`${item.srNo || idx}-previous`}
                    style={{ textAlign: "center" }}
                  >
                    <td>
                      <p>{item.srNo || idx + 1}</p>
                    </td>
                    <td>
                      <p>{item.particulars || "-"}</p>
                    </td>
                    <td>
                      <p>{item.unit || "-"}</p>
                    </td>
                    <td>
                      <p>{item.quantity ?? "-"}</p>
                    </td>
                    <td>
                      <p>{item.currency || "-"}</p>
                    </td>
                    {/* <td>
                      <p>{formatAmount(item.exchangeRate || 1)}</p>
                    </td> */}
                    <td>
                      <p>{formatAmount(item.rate)}</p>
                    </td>
                    <td>
                      <p>{item.remark || "-"}</p>
                    </td>
                    <td>
                      <p>{formatAmount(item.total)}</p>
                    </td>
                  </tr>
                ))}
              {Object.entries(previousCurrencyTotals).map(
                ([currencyName, total]) => (
                  <tr
                    key={`previous-total-${currencyName}`}
                    style={{ backgroundColor: "#d8e9f0", fontWeight: "bold" }}
                  >
                    <td
                      colSpan={7}
                      style={{ textAlign: "right", padding: "20px" }}
                    >
                      {`Grand Total (${currencyName})`}
                    </td>
                    <td style={{ width: "150px", textAlign: "center" }}>
                      {formatAmount(total)}
                    </td>
                  </tr>
                ),
              )}
              <tr style={{ backgroundColor: "#d8e9f0", fontWeight: "bold" }}>
                <td colSpan={7} style={{ textAlign: "right", padding: "20px" }}>
                  Grand Total (in INR):
                </td>
                <td style={{ width: "150px", textAlign: "center" }}>
                  {formatAmount(previousGrandTotalInInr)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <div className="dashboard-container">
        <table
          border={1}
          style={{
            borderCollapse: "collapse",
            minWidth: "100%",
            marginTop: "10px",
            textAlign: "center",
          }}
        >
          <thead>
            <tr>
              <th style={{ padding: "20px" }}>Sr.</th>
              <th style={{ width: "240px" }}>Particulars</th>
              <th style={{ width: "140px" }}>UOM</th>
              <th style={{ width: "140px" }}>Quantity</th>
              <th style={{ width: "180px" }}>Currency</th>
              <th>Ex. Rate</th>
              <th style={{ width: "150px" }}>Rate</th>
              <th>Total (INR)</th>
              <th style={{ width: "220px" }}>Remark</th>
              <th style={{ width: "90px" }}>Action</th>
            </tr>
          </thead>

          <tbody>
            {sortedItems.length === 0 ? (
              <tr>
                <td
                  colSpan={10}
                  style={{
                    padding: "20px",
                    textAlign: "center",
                    color: "#888",
                  }}
                >
                  No work items found.
                </td>
              </tr>
            ) : (
              items
                .sort((a, b) => a.itemOrder - b.itemOrder)
                .map((item, idx) => (
                  <tr key={idx}>
                    <td>
                      <p>{idx + 1}</p>
                    </td>

                    <td>
                      <input
                        className="table-input"
                        type="text"
                        value={item.particulars || ""}
                        disabled={!item.isAdditionalItem}
                        onChange={(e) =>
                          handleTextChange(idx, "particulars", e.target.value)
                        }
                        placeholder="Enter particulars"
                      />
                    </td>

                    <td>
                      <Autocomplete
                        className="unit-dropdown"
                        options={units}
                        value={item.unit || null}
                        onChange={(event, newValue) =>
                          handleTextChange(idx, "unit", newValue || "")
                        }
                        disablePortal
                        size="small"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Choose Unit"
                            variant="outlined"
                            size="small"
                            sx={dropdownSx}
                          />
                        )}
                        disableClearable
                        sx={{ width: "100%" }}
                        slotProps={{ paper: paperSx }}
                      />
                    </td>

                    <td>
                      <input
                        className="table-input"
                        type="number"
                        value={item.quantity ?? ""}
                        onChange={(e) =>
                          handleTextChange(idx, "quantity", e.target.value)
                        }
                        placeholder="0.00"
                        step={0.01}
                        min={0}
                      />
                    </td>

                    <td>
                      <Autocomplete
                        className="unit-dropdown"
                        options={currencyOptions}
                        getOptionLabel={(option) =>
                          typeof option === "string" ? option : option.name
                        }
                        value={
                          currencyOptions.find(
                            (opt) => opt.name === item.currency,
                          ) || null
                        }
                        onChange={(event, newValue) =>
                          handleCurrencyChange(
                            idx,
                            newValue ? newValue.name : "",
                          )
                        }
                        isOptionEqualToValue={(option, value) =>
                          option.name === value.name
                        }
                        disablePortal
                        size="small"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Choose Currency"
                            variant="outlined"
                            size="small"
                            sx={dropdownSx}
                          />
                        )}
                        filterSelectedOptions
                        autoHighlight
                        selectOnFocus
                        clearOnBlur={false}
                        handleHomeEndKeys
                        disableClearable
                        sx={{ width: "100%" }}
                        slotProps={{ paper: paperSx }}
                      />
                    </td>

                    <td>
                      <p>{item.exchangeRate ?? "-"}</p>
                    </td>

                    <td>
                      <input
                        className="table-input"
                        type="number"
                        value={item.rate}
                        onChange={(e) => handleRateChange(idx, e.target.value)}
                        placeholder="0.00"
                        step={0.01}
                        min={0}
                      />
                    </td>

                    <td style={{ textAlign: "center" }}>
                      <p>{formatAmount(item.total)}</p>
                    </td>

                    <td>
                      <input
                        className="table-input"
                        type="text"
                        value={item.remark || ""}
                        onChange={(e) =>
                          handleTextChange(idx, "remark", e.target.value)
                        }
                        placeholder="Enter remark"
                      />
                    </td>

                    <td>
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteRow(idx)}
                        disabled={sortedItems.length === 1}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </td>
                  </tr>
                ))
            )}

            {Object.entries(currencyTotals).map(([currencyName, total]) => (
              <tr
                key={`current-total-${currencyName}`}
                style={{ backgroundColor: "#d8e9f0", fontWeight: "bold" }}
              >
                <td colSpan={8} style={{ textAlign: "right", padding: "20px" }}>
                  {`Grand Total (${currencyName})`}
                </td>
                <td style={{ width: "150px", textAlign: "center" }}>
                  {formatAmount(total)}
                </td>
                <td></td>
              </tr>
            ))}

            <tr style={{ backgroundColor: "#d8e9f0", fontWeight: "bold" }}>
              <td colSpan={8} style={{ textAlign: "right", padding: "20px" }}>
                Grand Total (in INR):
              </td>
              <td style={{ width: "150px", textAlign: "center" }}>
                {formatAmount(grandTotalInInr)}
              </td>
              <td></td>
            </tr>
          </tbody>
        </table>

        <Typography component="p" fontWeight={700} fontSize={"14px"}>
          [Important Note:
          <br />
          - Please select a currency for each work item before proceeding.
          <br />
          - Rate can be entered as 0.00 if needed.
          <br />- Quantity must be greater than 0 for every line item.]
        </Typography>

        <div
          style={{
            display: "flex",
            justifyContent: "end",
            alignItems: "center",
            gap: 10,
          }}
        >
          <Button
            className="btn-outlined"
            onClick={handleAddRow}
            sx={{ padding: "5px 40px !important" }}
          >
            Add Work Item
          </Button>
        </div>
        <div
          style={{
            display: "flex",
            // flexDirection: "column",
            justifyContent: "end",
            // alignItems: "end",
            gap: 10,
          }}
        >
          <Button className="btn-outlined" onClick={handleFileSelect}>
            Upload Excel
          </Button>
          <Button className="btn-outlined" onClick={exportToExcel}>
            Download Excel
          </Button>
        </div>
        <input
          type="file"
          accept=".xlsx, .xls"
          ref={fileInputRef}
          onChange={importFromExcel}
          style={{ display: "none" }}
        />
      </div>

      <Box className="btn-container" sx={{ gap: 3 }}>
        <Button className="btn-outlined" onClick={() => setStep(1)}>
          Back
        </Button>
        <Button className="btn-contained" onClick={handleNext}>
          Next
        </Button>
      </Box>

      <ErrorModal
        open={errorModal}
        handleClose={() => setErrorModal(false)}
        msg={errorMsg}
      />
    </div>
  );
};

export default LogisticStep2;
